import { useEffect, useState, useCallback } from "react";
import {
  Building2, Check, X, Pencil, Pause, Play, Ban, Trash2, Store, KeyRound, Save,
} from "lucide-react";
import { toast } from "sonner";
import GalleryManager from "../components/GalleryManager";
import api, { fmtErr, peso, fmtDateTime } from "../lib/api";
import { refreshSiteContent } from "../lib/siteContent";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-700",
  suspended: "bg-amber-100 text-amber-700",
  deactivated: "bg-slate-200 text-slate-600",
  pending: "bg-blue-100 text-blue-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};
const PERM_LABELS = {
  manage_staff: "Add / remove staff",
  manage_inventory: "Manage inventory & products",
  manage_branches: "Manage branches",
};
const EMPTY_STORE = { store_name: "", business_type: "retail", owner_name: "", owner_username: "", owner_password: "", email: "" };
const EMPTY_USER = { name: "", username: "", password: "", role: "cashier", tenant_id: "", branch_id: "", email: "", status: "active", hourly_rate: "" };

function SecurityTab({ tenants }) {
  const [tenantId, setTenantId] = useState("");
  const [alerts, setAlerts] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAlerts = async (tid) => {
    try {
      const { data } = await api.get("/security/alerts", { headers: { "X-Tenant-Id": tid } });
      setAlerts(data.alerts);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const runIntegrity = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/integrity/run");
      setIntegrity(data);
      toast.success(`Integrity check complete — ${data.fixes.length} fixes, ${data.issues.length} issues`);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="security-tab">
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h3 className="font-heading font-semibold text-slate-900">Fraud Detection — per store</h3>
        <div className="flex gap-2">
          <Select value={tenantId} onValueChange={(v) => { setTenantId(v); loadAlerts(v); }}>
            <SelectTrigger className="w-56" data-testid="security-tenant-select"><SelectValue placeholder="Select store" /></SelectTrigger>
            <SelectContent>
              {tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.store_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {alerts && alerts.length === 0 && <p className="text-sm text-emerald-700">No suspicious activity detected today.</p>}
        {alerts?.map((a, i) => (
          <div key={i} className={`rounded-lg border px-3 py-2 text-xs ${a.severity === "high" ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid={`security-alert-${i}`}>
            <span className="font-bold uppercase">[{a.severity}]</span> {a.message}
          </div>
        ))}
        <p className="text-[11px] text-slate-400">Detects: excessive voids/refunds per cashier, &gt;30% discounts, large stock adjustments, brute-force login bursts.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h3 className="font-heading font-semibold text-slate-900">Self-Healing Integrity Check</h3>
        <p className="text-xs text-slate-500">Scans all tenants for data corruption and auto-repairs what is safe: negative stock → 0, orphaned products → reassigned to main branch. Unfixable issues (duplicate invoice numbers, orphan accounts) are reported.</p>
        <Button className="bg-slate-900" onClick={runIntegrity} disabled={busy} data-testid="integrity-run-btn">
          {busy ? "Running…" : "Run integrity check"}
        </Button>
        {integrity && (
          <div className="space-y-2 text-xs" data-testid="integrity-results">
            {integrity.fixes.map((f, i) => <p key={i} className="rounded bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-800">FIXED: {f}</p>)}
            {integrity.issues.map((f, i) => <p key={i} className="rounded bg-red-50 border border-red-200 px-3 py-2 text-red-800">ISSUE: {f}</p>)}
            {integrity.fixes.length === 0 && integrity.issues.length === 0 && (
              <p className="rounded bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-800">System is healthy — no corruption found.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [apps, setApps] = useState([]);
  const [branches, setBranches] = useState([]);
  const [perms, setPerms] = useState(null);
  const [staffLimits, setStaffLimits] = useState({});
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveCreds, setApproveCreds] = useState({ username: "", password: "" });
  const [storeForm, setStoreForm] = useState(null); // {editing: tenant|null, data}
  const [userForm, setUserForm] = useState(null); // {editing: user|null, data}
  const [confirm, setConfirm] = useState(null); // {title, body, action, testid}
  const [plans, setPlans] = useState([]);
  const [planDraft, setPlanDraft] = useState({});
  const [bizTypes, setBizTypes] = useState([]);
  const [newType, setNewType] = useState("");
  const [subExp, setSubExp] = useState({});
  const [subRequests, setSubRequests] = useState([]);
  const [moduleAccess, setModuleAccess] = useState(null);
  const [site, setSite] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, u, a, b, p, pl, bt, sr] = await Promise.all([
        api.get("/admin/tenants"), api.get("/admin/users"), api.get("/admin/applications"),
        api.get("/branches"), api.get("/admin/permissions"),
        api.get("/subscriptions/plans"), api.get("/admin/business-types"),
        api.get("/admin/subscription-requests"),
      ]);
      setTenants(t.data);
      setUsers(u.data);
      setApps(a.data);
      setBranches(b.data);
      setPerms(p.data.permissions);
      setModuleAccess(p.data.module_access || {});
      setPlans(pl.data);
      setPlanDraft(Object.fromEntries(pl.data.map((x) => [x.plan_id, { ...x, features: x.features.join("\n"), modules: x.modules || [] }])));
      setBizTypes(bt.data.types);
      setSubRequests(sr.data);
      setStaffLimits(Object.fromEntries(t.data.map((x) => [x.tenant_id, x.max_staff_allowed])));
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/config/site-content")
      .then(({ data }) => setSite({ ...data, tags: (data.tags || []).join(", ") }))
      .catch(() => {});
  }, []);

  const tenantName = (id) => tenants.find((t) => t.tenant_id === id)?.store_name || "—";

  const run = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const updateTenant = (t, payload, msg) => run(() => api.put(`/admin/tenants/${t.tenant_id}`, payload), msg);

  const saveSite = () => run(async () => {
    await api.put("/admin/site-content", {
      headline: site.headline, description: site.description,
      tags: site.tags.split(","), footer: site.footer,
    });
    await refreshSiteContent();
  }, "Site content updated");

  const submitStore = async (e) => {
    e.preventDefault();
    const { editing, data } = storeForm;
    if (editing) {
      const payload = { store_name: data.store_name, business_type: data.business_type };
      const la = parseFloat(data.latitude), ln = parseFloat(data.longitude);
      if (!isNaN(la) && !isNaN(ln)) { payload.latitude = la; payload.longitude = ln; }
      await run(() => api.put(`/admin/tenants/${editing.tenant_id}`, payload), "Store updated");
    } else {
      await run(() => api.post("/admin/tenants", { ...data, email: data.email || null }), "Store registered and owner credentials issued");
    }
    setStoreForm(null);
  };

  const submitUser = async (e) => {
    e.preventDefault();
    const { editing, data } = userForm;
    const payload = {
      name: data.name, username: data.username, role: data.role,
      tenant_id: data.tenant_id, branch_id: data.branch_id || null,
      email: data.email || null, status: data.status,
      hourly_rate: data.hourly_rate === "" ? null : Number(data.hourly_rate),
    };
    if (data.password) payload.password = data.password;
    if (editing) {
      await run(() => api.put(`/admin/users/${editing.user_id}`, payload), "User updated");
    } else {
      if (!data.password) { toast.error("Password is required"); return; }
      await run(() => api.post("/admin/users", payload), "User account created");
    }
    setUserForm(null);
  };

  const setUserStatus = (u, status) => run(() => api.put(`/admin/users/${u.user_id}`, { status }),
    `${u.name} ${status}`);

  const togglePerm = (role, perm, value) => {
    const next = { ...perms, [role]: { ...perms[role], [perm]: value } };
    setPerms(next);
    run(() => api.put("/admin/permissions", { permissions: next, module_access: moduleAccess }), "Permissions updated");
  };

  const setModuleLevel = (role, mod, level) => {
    const next = { ...moduleAccess, [role]: { ...moduleAccess[role], [mod]: level } };
    setModuleAccess(next);
    run(() => api.put("/admin/permissions", { permissions: perms, module_access: next }), "Module access updated");
  };

  const MODULE_LABELS = {
    dashboard: "Dashboard", pos: "Point of Sale", inventory: "Inventory", sales: "Invoice Register",
    orders: "Client Orders", attendance: "Attendance", clients: "Clients", staff: "Staff & Branches",
    payroll: "Payroll", bir: "BIR & Tax", subscription: "Subscription", audit: "Audit Trail",
  };

  const pendingApps = apps.filter((a) => a.status === "pending");
  const tenantUsers = users.filter((u) => u.role !== "system_admin");

  return (
    <div className="space-y-6" data-testid="admin-console">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Platform Admin Console</h1>
        <p className="text-sm text-slate-500">{tenants.length} stores · {users.length} users · {pendingApps.length} pending applications</p>
      </div>

      <Tabs defaultValue={pendingApps.length ? "applications" : "stores"}>
        <TabsList>
          <TabsTrigger value="applications" data-testid="admin-tab-applications">
            Applications{pendingApps.length > 0 && <span className="ml-1.5 rounded-full bg-blue-600 text-white text-[10px] px-1.5 py-0.5">{pendingApps.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="stores" data-testid="admin-tab-stores">Stores</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="permissions" data-testid="admin-tab-permissions">Role Permissions</TabsTrigger>
          <TabsTrigger value="plans" data-testid="admin-tab-plans">Subscription Plans</TabsTrigger>
          <TabsTrigger value="subrequests" data-testid="admin-tab-subrequests">Sub Requests</TabsTrigger>
          <TabsTrigger value="types" data-testid="admin-tab-types">Store Types</TabsTrigger>
          <TabsTrigger value="security" data-testid="admin-tab-security">Security</TabsTrigger>
          <TabsTrigger value="homepage" data-testid="admin-tab-homepage">Homepage</TabsTrigger>
        </TabsList>

        <TabsContent value="applications" className="mt-4 space-y-3">
          {pendingApps.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-400">No pending applications.</div>
          )}
          {pendingApps.map((a) => (
            <div key={a.application_id} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-wrap items-center gap-4" data-testid={`application-card-${a.application_id}`}>
              <div className="flex-1 min-w-48">
                <p className="font-semibold text-slate-900">{a.store_name} <span className="ml-1 rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase">{a.business_type}</span></p>
                <p className="text-xs text-slate-500 mt-0.5">{a.contact_name}{a.email ? ` · ${a.email}` : ""}{a.phone ? ` · ${a.phone}` : ""} · applied {fmtDateTime(a.created_at)}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-app-${a.application_id}`}
                  onClick={() => {
                    setApproveTarget(a);
                    setApproveCreds({ username: a.store_name.toLowerCase().replace(/[^a-z0-9]+/g, ".").replace(/^\.|\.$/g, "").slice(0, 24) || "owner", password: "" });
                  }}>
                  <Check className="h-4 w-4 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`disapprove-app-${a.application_id}`}
                  onClick={() => setConfirm({
                    title: "Disapprove application?",
                    body: `${a.store_name}'s application will be marked as rejected.`,
                    testid: "confirm-disapprove-app",
                    action: () => run(() => api.post(`/admin/applications/${a.application_id}/reject`), "Application rejected"),
                  })}>
                  <X className="h-4 w-4 mr-1" /> Disapprove
                </Button>
              </div>
            </div>
          ))}
          {apps.filter((a) => a.status !== "pending").slice(0, 5).map((a) => (
            <div key={a.application_id} className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex items-center gap-3 opacity-70">
              <p className="text-sm text-slate-600 flex-1">{a.store_name}</p>
              <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${STATUS_BADGE[a.status]}`}>{a.status.toUpperCase()}</span>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="stores" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="admin-create-store-btn"
              onClick={() => setStoreForm({ editing: null, data: { ...EMPTY_STORE } })}>
              <Store className="h-4 w-4 mr-1.5" /> Register Store
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4" data-testid="admin-tenant-table">
            {tenants.map((t) => {
              const status = t.status || "active";
              return (
                <div key={t.tenant_id} className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid={`tenant-card-${t.tenant_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-heading font-semibold text-slate-900">{t.store_name}</p>
                      <p className="text-xs text-slate-500 capitalize">{t.business_type} · registered {fmtDateTime(t.created_at)}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${STATUS_BADGE[status]}`} data-testid={`tenant-status-${t.tenant_id}`}>{status.toUpperCase()}</span>
                  </div>
                  <p className="text-xs text-slate-500 font-mono">
                    {t.branch_count} branches · {t.staff_count} staff · {t.product_count} products · {peso(t.sales_total)} lifetime sales
                  </p>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                      <Switch checked={t.bir_compliance_enabled} onCheckedChange={(v) => updateTenant(t, { bir_compliance_enabled: v }, "BIR compliance " + (v ? "enabled" : "disabled"))} data-testid={`admin-toggle-bir-compliance-${t.tenant_id}`} />
                      BIR Compliance
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                      <Switch checked={t.multi_branch_enabled} onCheckedChange={(v) => updateTenant(t, { multi_branch_enabled: v }, "Multi-branch " + (v ? "enabled" : "disabled"))} data-testid={`admin-toggle-multi-branch-${t.tenant_id}`} />
                      Multi-Branch
                    </label>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-slate-600">Max staff</span>
                      <Input type="number" min="1" className="w-16 h-8 font-mono" value={staffLimits[t.tenant_id] ?? t.max_staff_allowed}
                        onChange={(e) => setStaffLimits({ ...staffLimits, [t.tenant_id]: e.target.value })} data-testid={`admin-staff-limit-input-${t.tenant_id}`} />
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => updateTenant(t, { max_staff_allowed: Number(staffLimits[t.tenant_id]) || 1 }, "Staff limit updated")} data-testid={`admin-staff-limit-save-${t.tenant_id}`}>
                        <Save className="h-3.5 w-3.5 text-slate-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-600">Plan</span>
                    <Select value={(t.subscription || {}).plan_id || "free"}
                      onValueChange={(v) => updateTenant(t, { subscription_plan: v }, "Subscription plan updated")}>
                      <SelectTrigger className="w-32 h-8" data-testid={`admin-plan-select-${t.tenant_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {plans.map((p) => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-slate-600">Expires</span>
                    <Input type="date" className="w-36 h-8" data-testid={`admin-sub-expiry-input-${t.tenant_id}`}
                      value={subExp[t.tenant_id] ?? ((t.subscription || {}).expires_at || "").slice(0, 10)}
                      onChange={(e) => setSubExp({ ...subExp, [t.tenant_id]: e.target.value })} />
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Save expiry"
                      onClick={() => updateTenant(t, { subscription_expires_at: subExp[t.tenant_id] ? `${subExp[t.tenant_id]}T23:59:59+00:00` : "" }, "Subscription expiry updated")}
                      data-testid={`admin-sub-expiry-save-${t.tenant_id}`}>
                      <Save className="h-3.5 w-3.5 text-slate-500" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
                    <Button size="sm" variant="outline" className="mt-2" data-testid={`edit-store-${t.tenant_id}`}
                      onClick={() => setStoreForm({ editing: t, data: { ...EMPTY_STORE, store_name: t.store_name, business_type: t.business_type, latitude: t.latitude != null ? String(t.latitude) : "", longitude: t.longitude != null ? String(t.longitude) : "" } })}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    {status === "active" && (
                      <Button size="sm" variant="outline" className="mt-2 text-amber-700 border-amber-200 hover:bg-amber-50" data-testid={`suspend-store-${t.tenant_id}`}
                        onClick={() => updateTenant(t, { status: "suspended" }, "Store suspended")}>
                        <Pause className="h-3.5 w-3.5 mr-1" /> Suspend
                      </Button>
                    )}
                    {status !== "active" && (
                      <Button size="sm" variant="outline" className="mt-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`activate-store-${t.tenant_id}`}
                        onClick={() => updateTenant(t, { status: "active" }, "Store activated")}>
                        <Play className="h-3.5 w-3.5 mr-1" /> Activate
                      </Button>
                    )}
                    {status !== "deactivated" && (
                      <Button size="sm" variant="outline" className="mt-2 text-slate-600" data-testid={`deactivate-store-${t.tenant_id}`}
                        onClick={() => updateTenant(t, { status: "deactivated" }, "Store deactivated")}>
                        <Ban className="h-3.5 w-3.5 mr-1" /> Deactivate
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="mt-2 text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-store-${t.tenant_id}`}
                      onClick={() => setConfirm({
                        title: "Delete store permanently?",
                        body: `${t.store_name} and ALL its branches, users, products, sales and attendance data will be deleted. This cannot be undone.`,
                        testid: "confirm-delete-store",
                        action: () => run(() => api.delete(`/admin/tenants/${t.tenant_id}`), "Store deleted"),
                      })}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              );
            })}
            {tenants.length === 0 && (
              <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-400">No stores registered yet.</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="admin-create-user-btn"
              onClick={() => setUserForm({ editing: null, data: { ...EMPTY_USER } })}>
              <KeyRound className="h-4 w-4 mr-1.5" /> Issue Credentials
            </Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenantUsers.map((u) => {
                  const st = u.status || "active";
                  return (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell>
                        <p className="font-medium text-slate-900 text-sm">{u.name}</p>
                        <p className="font-mono text-xs text-slate-400">{u.username || u.email}</p>
                      </TableCell>
                      <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold capitalize">{u.role}</span></TableCell>
                      <TableCell className="text-sm text-slate-600">{tenantName(u.tenant_id)}</TableCell>
                      <TableCell><span className={`rounded px-2 py-0.5 text-[10px] font-bold ${STATUS_BADGE[st] || STATUS_BADGE.pending}`} data-testid={`user-status-${u.user_id}`}>{st.toUpperCase()}</span></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" title="Edit / reset password" data-testid={`edit-user-${u.user_id}`}
                            onClick={() => setUserForm({ editing: u, data: { name: u.name, username: u.username || "", password: "", role: u.role, tenant_id: u.tenant_id || "", branch_id: u.branch_id || "", email: u.email || "", status: st, hourly_rate: u.hourly_rate ?? "" } })}>
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                          {st === "active" ? (
                            <Button variant="ghost" size="icon" title="Suspend" data-testid={`suspend-user-${u.user_id}`} onClick={() => setUserStatus(u, "suspended")}>
                              <Pause className="h-4 w-4 text-amber-600" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="icon" title="Activate" data-testid={`activate-user-${u.user_id}`} onClick={() => setUserStatus(u, "active")}>
                              <Play className="h-4 w-4 text-emerald-600" />
                            </Button>
                          )}
                          {st !== "deactivated" && (
                            <Button variant="ghost" size="icon" title="Deactivate" data-testid={`deactivate-user-${u.user_id}`} onClick={() => setUserStatus(u, "deactivated")}>
                              <Ban className="h-4 w-4 text-slate-500" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" title="Delete" data-testid={`delete-user-${u.user_id}`}
                            onClick={() => setConfirm({
                              title: "Delete user?",
                              body: `${u.name} (${u.username || u.email}) will be permanently deleted.`,
                              testid: "confirm-delete-user",
                              action: () => run(() => api.delete(`/admin/users/${u.user_id}`), "User deleted"),
                            })}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {tenantUsers.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-sm text-slate-400">No user accounts yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="permissions" className="mt-4 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden max-w-2xl">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-slate-900">Action Permissions</h3>
              <p className="text-xs text-slate-500">Control what each operational role can do. Changes apply immediately.</p>
            </div>
            {perms && (
              <Table data-testid="permissions-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Role</TableHead>
                    {Object.values(PERM_LABELS).map((l) => <TableHead key={l} className="text-center">{l}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {["owner", "manager", "cashier"].map((role) => (
                    <TableRow key={role}>
                      <TableCell className="font-medium capitalize text-slate-900">{role}</TableCell>
                      {Object.keys(PERM_LABELS).map((perm) => (
                        <TableCell key={perm} className="text-center">
                          <Switch checked={!!perms[role]?.[perm]} onCheckedChange={(v) => togglePerm(role, perm, v)} data-testid={`perm-${role}-${perm}`} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden max-w-3xl">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-slate-900">Module Access Levels</h3>
              <p className="text-xs text-slate-500">
                <span className="font-semibold">None</span> hides the module entirely · <span className="font-semibold">View</span> read-only · <span className="font-semibold">Manage</span> full access. Enforced in the API and the UI.
              </p>
            </div>
            {moduleAccess && (
              <Table data-testid="module-access-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Module</TableHead>
                    <TableHead className="text-center">Owner</TableHead>
                    <TableHead className="text-center">Manager</TableHead>
                    <TableHead className="text-center">Cashier</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(MODULE_LABELS).map(([mod, label]) => (
                    <TableRow key={mod}>
                      <TableCell className="font-medium text-slate-900">{label}</TableCell>
                      {["owner", "manager", "cashier"].map((role) => (
                        <TableCell key={role} className="text-center">
                          <Select value={moduleAccess[role]?.[mod] || "none"} onValueChange={(v) => setModuleLevel(role, mod, v)}>
                            <SelectTrigger className="w-28 h-8 mx-auto text-xs" data-testid={`module-access-${role}-${mod}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="view">View</SelectItem>
                              <SelectItem value="manage">Manage</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="plans" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4" data-testid="admin-plans-grid">
            {plans.map((p) => {
              const d = planDraft[p.plan_id] || { ...p, features: (p.features || []).join("\n") };
              const setD = (k, v) => setPlanDraft({ ...planDraft, [p.plan_id]: { ...d, [k]: v } });
              return (
                <div key={p.plan_id} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid={`plan-edit-${p.plan_id}`}>
                  <h4 className="font-heading font-bold text-slate-900" data-testid={`plan-heading-${p.plan_id}`}>{d.name || p.name}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Plan name</Label>
                      <Input value={d.name} onChange={(e) => setD("name", e.target.value)} data-testid={`plan-name-${p.plan_id}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Price (₱/month)</Label>
                      <Input type="number" min="0" value={d.price_monthly} onChange={(e) => setD("price_monthly", e.target.value)} data-testid={`plan-price-${p.plan_id}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Max branches</Label>
                      <Input type="number" min="1" value={d.max_branches ?? ""} onChange={(e) => setD("max_branches", e.target.value)} data-testid={`plan-branches-${p.plan_id}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Max users</Label>
                      <Input type="number" min="1" value={d.max_users ?? ""} onChange={(e) => setD("max_users", e.target.value)} data-testid={`plan-users-${p.plan_id}`} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Features (one per line)</Label>
                    <textarea className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm min-h-24" value={d.features} onChange={(e) => setD("features", e.target.value)} data-testid={`plan-features-${p.plan_id}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Included modules (subscription-level gating)</Label>
                    <div className="grid grid-cols-3 gap-1.5" data-testid={`plan-modules-${p.plan_id}`}>
                      {Object.entries(MODULE_LABELS).map(([m, l]) => (
                        <label key={m} className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                          <input type="checkbox" className="accent-slate-900"
                            checked={(d.modules || []).includes(m)}
                            onChange={(e) => setD("modules", e.target.checked ? [...(d.modules || []), m] : (d.modules || []).filter((x) => x !== m))}
                            data-testid={`plan-module-${p.plan_id}-${m}`} />
                          {l}
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button size="sm" className="bg-slate-900" data-testid={`plan-save-${p.plan_id}`}
                    onClick={() => run(() => api.post("/admin/plans", {
                      name: d.name, price_monthly: Number(d.price_monthly) || 0,
                      max_branches: d.max_branches === "" ? null : Number(d.max_branches),
                      max_users: d.max_users === "" ? null : Number(d.max_users),
                      features: d.features.split("\n").map((f) => f.trim()).filter(Boolean),
                      modules: d.modules,
                    }), "Plan saved")}>
                    <Save className="h-3.5 w-3.5 mr-1" /> Save plan
                  </Button>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="types" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-xl space-y-4" data-testid="admin-store-types">
            <h3 className="font-heading font-semibold text-slate-900">Store Types</h3>
            <p className="text-xs text-slate-500">Available in registration and store creation. Types in use by existing stores cannot be removed.</p>
            <div className="flex flex-wrap gap-2">
              {bizTypes.map((t) => (
                <span key={t} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium capitalize" data-testid={`store-type-${t}`}>
                  {t.replace(/_/g, " ")}
                  <button className="text-slate-400 hover:text-red-600" title="Remove"
                    onClick={() => setConfirm({
                      title: `Remove store type "${t}"?`,
                      body: "Stores already using this type keep it, but it will no longer be offered for new stores.",
                      testid: "confirm-remove-type",
                      action: () => run(() => api.delete(`/admin/business-types/${t}`), "Type removed"),
                    })}
                    data-testid={`remove-type-${t}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="New type (e.g. restaurant)" value={newType} onChange={(e) => setNewType(e.target.value)} data-testid="new-store-type-input" />
              <Button className="bg-slate-900" data-testid="add-store-type-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/business-types", { type: newType });
                    const added = data.types[data.types.length - 1];
                    toast.success(`Store type added: ${added}`);
                    setNewType("");
                    load();
                  } catch (e) { toast.error(fmtErr(e)); }
                }}>
                Add type
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="subrequests" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="sub-requests-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Store</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subRequests.map((r) => (
                  <TableRow key={r.request_id} data-testid={`sub-request-${r.request_id}`}>
                    <TableCell className="font-medium text-slate-900">{r.store_name}</TableCell>
                    <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-bold">{r.plan_name}</span></TableCell>
                    <TableCell className="text-sm text-slate-600">{r.duration_months > 0 ? `${r.duration_months} mo` : "14-day trial"}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{peso(r.amount)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.requested_by} · {r.created_at?.slice(0, 10)}</TableCell>
                    <TableCell><span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${r.status === "pending" ? "bg-blue-100 text-blue-700" : r.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.status}</span></TableCell>
                    <TableCell className="text-right">
                      {r.status === "pending" && (
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid={`approve-sub-${r.request_id}`}
                            onClick={() => run(() => api.post(`/admin/subscription-requests/${r.request_id}/approve`), "Subscription activated")}>
                            <Check className="h-3.5 w-3.5 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200" data-testid={`reject-sub-${r.request_id}`}
                            onClick={() => run(() => api.post(`/admin/subscription-requests/${r.request_id}/reject`), "Request rejected")}>
                            <X className="h-3.5 w-3.5 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {subRequests.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-sm text-slate-400">No subscription requests.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="security" className="mt-4">
          <SecurityTab tenants={tenants} />
        </TabsContent>

        <TabsContent value="homepage" className="mt-4 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 max-w-3xl" data-testid="site-content-card">
            <h3 className="font-heading font-semibold text-slate-900">Login Page Hero &amp; Brand Logo</h3>
            <p className="text-sm text-slate-500">Wording shown on the left panel of the sign-in page, and the logo used across the app and public portal. The current text stays as the default until you change it here.</p>
            {site && (
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Headline</Label><Input value={site.headline} onChange={(e) => setSite({ ...site, headline: e.target.value })} data-testid="site-headline-input" /></div>
                <div className="space-y-1.5"><Label>Description</Label><Input value={site.description} onChange={(e) => setSite({ ...site, description: e.target.value })} data-testid="site-description-input" /></div>
                <div className="space-y-1.5"><Label>Feature tags (comma-separated)</Label><Input value={site.tags} onChange={(e) => setSite({ ...site, tags: e.target.value })} data-testid="site-tags-input" /></div>
                <div className="space-y-1.5"><Label>Footer line</Label><Input value={site.footer} onChange={(e) => setSite({ ...site, footer: e.target.value })} data-testid="site-footer-input" /></div>
                <Button className="bg-slate-900" onClick={saveSite} data-testid="site-content-save-btn"><Save className="h-4 w-4 mr-2" /> Save site content</Button>
              </div>
            )}
            <div className="border-t border-slate-100 pt-4">
              <GalleryManager platform kinds={["logo"]} testid="logo-gallery" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 max-w-3xl" data-testid="homepage-gallery-card">
            <h3 className="font-heading font-semibold text-slate-900">Homepage Feature Photos</h3>
            <p className="text-sm text-slate-500">
              Platform-level photos shown in the public portal homepage showcase, visible to all visitors.
              Max 10 photos, 5 MB each (JPG, PNG, WEBP, GIF). Store-specific photos are managed by each store in their Settings.
            </p>
            <GalleryManager platform kinds={["feature"]} testid="homepage-gallery" />
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="approve-dialog">
          <DialogHeader><DialogTitle className="font-heading">Approve — {approveTarget?.store_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Issue the owner's login credentials. Share them with the applicant through your contact channel.</p>
            <div className="space-y-1.5">
              <Label>Owner username</Label>
              <Input value={approveCreds.username} onChange={(e) => setApproveCreds({ ...approveCreds, username: e.target.value })} data-testid="approve-username-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Owner password</Label>
              <Input value={approveCreds.password} onChange={(e) => setApproveCreds({ ...approveCreds, password: e.target.value })} placeholder="min 6 chars" data-testid="approve-password-input" />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="approve-confirm-btn"
              onClick={() => run(async () => {
                await api.post(`/admin/applications/${approveTarget.application_id}/approve`, approveCreds);
                setApproveTarget(null);
              }, "Store provisioned and credentials issued")}>
              Approve &amp; provision store
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!storeForm} onOpenChange={(o) => !o && setStoreForm(null)}>
        <DialogContent className="max-w-md" data-testid="store-form-modal">
          <DialogHeader><DialogTitle className="font-heading">{storeForm?.editing ? "Edit Store" : "Register Store"}</DialogTitle></DialogHeader>
          {storeForm && (
            <form onSubmit={submitStore} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Store name</Label>
                <Input value={storeForm.data.store_name} onChange={(e) => setStoreForm({ ...storeForm, data: { ...storeForm.data, store_name: e.target.value } })} required data-testid="store-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Business type</Label>
                <Select value={storeForm.data.business_type} onValueChange={(v) => setStoreForm({ ...storeForm, data: { ...storeForm.data, business_type: v } })}>
                  <SelectTrigger data-testid="store-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {bizTypes.map((b) => <SelectItem key={b} value={b} className="capitalize">{b.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {storeForm.editing && (
                <div className="space-y-1.5">
                  <Label>Store coordinates <span className="text-slate-400 font-normal">(powers "Near me" in the public portal)</span></Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Input value={storeForm.data.latitude} onChange={(e) => setStoreForm({ ...storeForm, data: { ...storeForm.data, latitude: e.target.value } })} placeholder="Latitude e.g. 14.5995" className="font-mono" data-testid="store-latitude-input" />
                    <Input value={storeForm.data.longitude} onChange={(e) => setStoreForm({ ...storeForm, data: { ...storeForm.data, longitude: e.target.value } })} placeholder="Longitude e.g. 120.9842" className="font-mono" data-testid="store-longitude-input" />
                  </div>
                </div>
              )}
              {!storeForm.editing && (
                <>
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Owner credentials (issued by admin)</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Owner full name</Label>
                    <Input value={storeForm.data.owner_name} onChange={(e) => setStoreForm({ ...storeForm, data: { ...storeForm.data, owner_name: e.target.value } })} required data-testid="store-owner-name-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Username</Label>
                      <Input value={storeForm.data.owner_username} onChange={(e) => setStoreForm({ ...storeForm, data: { ...storeForm.data, owner_username: e.target.value } })} required data-testid="store-owner-username-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Password</Label>
                      <Input value={storeForm.data.owner_password} onChange={(e) => setStoreForm({ ...storeForm, data: { ...storeForm.data, owner_password: e.target.value } })} required data-testid="store-owner-password-input" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Input type="email" value={storeForm.data.email} onChange={(e) => setStoreForm({ ...storeForm, data: { ...storeForm.data, email: e.target.value } })} data-testid="store-owner-email-input" />
                  </div>
                </>
              )}
              <Button type="submit" className="w-full bg-slate-900" data-testid="store-save-btn">
                {storeForm.editing ? "Save changes" : "Register store & issue credentials"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!userForm} onOpenChange={(o) => !o && setUserForm(null)}>
        <DialogContent className="max-w-md" data-testid="user-form-modal">
          <DialogHeader><DialogTitle className="font-heading">{userForm?.editing ? `Edit — ${userForm.editing.name}` : "Issue User Credentials"}</DialogTitle></DialogHeader>
          {userForm && (
            <form onSubmit={submitUser} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input value={userForm.data.name} onChange={(e) => setUserForm({ ...userForm, data: { ...userForm.data, name: e.target.value } })} required data-testid="user-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Username</Label>
                  <Input value={userForm.data.username} onChange={(e) => setUserForm({ ...userForm, data: { ...userForm.data, username: e.target.value } })} data-testid="user-username-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{userForm.editing ? "Reset password (blank = keep)" : "Password"}</Label>
                  <Input value={userForm.data.password} onChange={(e) => setUserForm({ ...userForm, data: { ...userForm.data, password: e.target.value } })} data-testid="user-password-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={userForm.data.role} onValueChange={(v) => setUserForm({ ...userForm, data: { ...userForm.data, role: v } })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="cashier">Cashier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Store</Label>
                  <Select value={userForm.data.tenant_id} onValueChange={(v) => setUserForm({ ...userForm, data: { ...userForm.data, tenant_id: v, branch_id: "" } })}>
                    <SelectTrigger data-testid="user-tenant-select"><SelectValue placeholder="Select store" /></SelectTrigger>
                    <SelectContent>
                      {tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.store_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Branch</Label>
                  <Select value={userForm.data.branch_id} onValueChange={(v) => setUserForm({ ...userForm, data: { ...userForm.data, branch_id: v } })}>
                    <SelectTrigger data-testid="user-branch-select"><SelectValue placeholder="Default branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.filter((b) => b.tenant_id === userForm.data.tenant_id).map((b) => (
                        <SelectItem key={b.branch_id} value={b.branch_id}>{b.branch_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={userForm.data.status} onValueChange={(v) => setUserForm({ ...userForm, data: { ...userForm.data, status: v } })}>
                    <SelectTrigger data-testid="user-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="deactivated">Deactivated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Email <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Input type="email" value={userForm.data.email} onChange={(e) => setUserForm({ ...userForm, data: { ...userForm.data, email: e.target.value } })} data-testid="user-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Hourly rate (₱/hr)</Label>
                  <Input type="number" min="0" step="0.01" value={userForm.data.hourly_rate} onChange={(e) => setUserForm({ ...userForm, data: { ...userForm.data, hourly_rate: e.target.value } })} data-testid="user-hourly-rate-input" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-slate-900" data-testid="user-save-btn">
                {userForm.editing ? "Save changes" : "Create account"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid={confirm?.testid}
              onClick={async () => { await confirm.action(); setConfirm(null); }}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
